import axios from 'axios';
import { REST_HOSTNAME } from '../Config';

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

export interface IUserActivity {
    id: number;
    user_id: number;
    activity_name: string;
    start_time: Date;
    end_time: Date;
    activity_sec: number;
    subactivity_name: string | null;
    session_id: string | null;
}

export interface IUserActivitySummary {
    ai_chat_sec: number;
    comment_sec: number;
    custom_sentence_sec: number;
    home_sec: number;
    investor_sec: number;
    leaderboard_sec: number;
    lesson_sec: number;
    course_sec: number;
    settings_sec: number;
    activity_sec: number;
    distinct_day_count: number;
}

export type UserActivityReason = "interval" | "hidden" | "unmount" | "switch" | "idle";

export const UserActivityApi = {
    getAll: function (userId: number) {
        return axiosInstance.request<IUserActivity[]>({
            method: "GET",
            url: `/user_activity?user_id=${userId}`,
            transformResponse: [(data) => {
                const raw = JSON.parse(data);
                return raw.map((item: any) => ({
                    ...item,
                    start_time: new Date(item.start_time),
                    end_time: new Date(item.end_time),
                }));
            }]
        });
    },
    create: function (userActivity: IUserActivity) {
        return axiosInstance.request<IUserActivity>({
            method: "POST",
            url: `/user_activity`,
            data: userActivity
        });
    },
    async updateBeacon(activity: IUserActivity, reason: UserActivityReason): Promise<boolean> {
        const jwt = localStorage.getItem("jwt") || undefined;
        const url = `${REST_HOSTNAME}/current_user_activity`;

        // Dates to ISO strings just in case they're Date objects
        const bodyObj = {
            ...activity,
            // start_time: new Date(activity.start_time).toISOString(),
            // end_time: new Date(activity.end_time).toISOString(),
            jwt,
            reason, // optional, useful for debugging on server side
        };

        const json = JSON.stringify(bodyObj);
        // Soft limit: avoid huge payloads that browsers may drop with keepalive/beacon
        if (json.length > 60000) {
            throw Error(`UserActivity payload too large, dropping: ${json.length}`)
            // console.warn("UserActivity payload too large, dropping:", json.length);
            // return false;
        }

        // If we're leaving the page (hidden/unmount), prefer beacon
        const preferBeacon = reason === "hidden" || reason === "unmount";

        if (preferBeacon && typeof navigator.sendBeacon === "function") {
            // const blob = new Blob([json], { type: "application/json" });
            const blob = new Blob([json], { type: "text/plain;charset=UTF-8" }); // safelisted
            const responseOk = navigator.sendBeacon(url, blob);
            if (responseOk) return true;
            // Fall through to fetch if beacon refused the send
        }

        // Use fetch for interval/idle/switch or as a fallback
        try {
            const abortController = new AbortController();
            // Keep this very short; keepalive requests must finish quickly and are best-effort
            const timeout = setTimeout(() => abortController.abort(), 4000);

            const result = await fetch(url, {
                method: "POST",
                body: json,
                headers: { "Content-Type": "application/json" },
                keepalive: true,  // allows the request to continue during page unloads
                mode: "cors",
                credentials: "omit", // you’re sending jwt in body; change if you use cookies
                signal: abortController.signal,
            });
            clearTimeout(timeout);
            return result.ok;
        } catch (err) {
            console.warn("UserActivity send failed:", err);
            return false;
        }
    }
}
